import { Text } from '@fluentui/react-components';
import { DoorRegular, PeopleRegular, PersonRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { isNotOccurringMotive } from '../../Utils/substitution/notOccurringMotives';
import { SubstitutionBadge } from './SubstitutionBadge';
import './SubstitutionCard.scss';
import { SubstitutionEntry, SubstitutionMotive } from './SubstitutionWidget.interface';

export interface SubstitutionCardProps {
  entry: SubstitutionEntry;
}

const getDefaultBorderColor = (motives: SubstitutionMotive[]): string => {
  if (motives.includes('ShiftedFrom') || motives.includes('ShiftedTo')) {
    return 'var(--colorStatusSuccessBorder1)';
  }
  if (isNotOccurringMotive(motives)) {
    return 'var(--colorStatusDangerBorder1)';
  }
  return 'var(--colorBrandBackground)';
};

export const SubstitutionCard: FC<SubstitutionCardProps> = ({ entry }) => {
  const { t } = useTranslation();
  const borderColor = entry.borderColor ?? getDefaultBorderColor(entry.motives);
  const isCancelled = isNotOccurringMotive(entry.motives);

  const hasRoomChange = entry.originalRoom && entry.originalRoom !== entry.room;
  const hasTeacherChange = entry.originalTeacher && entry.originalTeacher !== entry.teacher;

  return (
    <div
      className={cn('substitutionCard', { 'substitutionCard--cancelled': isCancelled })}
      style={{ borderLeftColor: borderColor }}
    >
      <div className="flex flex-col gap-2 substitutionCard__header">
        <Text className="substitutionCard__subject" weight="bold" size={400}>
          {entry.subject}
        </Text>
        <div className="flex flex-wrap gap-1">
          {entry.motives.map((motive, index) => (
            <SubstitutionBadge key={`${motive}-${index}`} motive={motive} />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        {entry.className && (
          <div className="flex items-center gap-2">
            <PeopleRegular className="substitutionCard__icon" />
            <Text className="substitutionCard__label">
              {t('dashboard:substitution:card:class', { defaultValue: 'Klasse' })}
            </Text>
            <Text className="substitutionCard__value">{entry.className}</Text>
          </div>
        )}

        {(entry.room || entry.originalRoom) && (
          <div className="flex items-center gap-2">
            <DoorRegular className="substitutionCard__icon" />
            <Text className="substitutionCard__label">
              {t('dashboard:substitution:card:room', { defaultValue: 'Raum' })}
            </Text>
            <div className="flex items-center gap-1">
              {hasRoomChange && (
                <Text className="substitutionCard__value substitutionCard__value--strikethrough">
                  {entry.originalRoom}
                </Text>
              )}
              <Text className="substitutionCard__value">{entry.room}</Text>
            </div>
          </div>
        )}

        {(entry.teacher || entry.originalTeacher) && (
          <div className="flex items-center gap-2">
            <PersonRegular className="substitutionCard__icon" />
            <Text className="substitutionCard__label">
              {t('dashboard:substitution:card:teacher', { defaultValue: 'Lehrer' })}
            </Text>
            <div className="flex items-center gap-1">
              {hasTeacherChange && (
                <Text className="substitutionCard__value substitutionCard__value--strikethrough">
                  {entry.originalTeacher}
                </Text>
              )}
              <Text className="substitutionCard__value">{entry.teacher}</Text>
            </div>
          </div>
        )}
      </div>

      {entry.remark && (
        <Text className="substitutionCard__remark" size={200}>
          {entry.remark}
        </Text>
      )}
    </div>
  );
};
